// src/components/FinancialTab.js
import React, { useEffect, useMemo, useState } from 'react';
import { DollarSign, Receipt, CheckCircle, Plus, WalletCards, Lock, Sparkles } from 'lucide-react';
import api from '../services/api';
import ImportedNfseViewer from '../components/billing/ImportedNfseViewer';
import FinancialAIReviewCard from './ai/FinancialAIReviewCard';
import { useAuth } from '../contexts/AuthContext';

function resolvePatientId(props) {
  const fromSelected = props?.selectedPatient?.id;
  const fromProp =
    props?.patientId ||
    props?.patient_id ||
    props?.patient?.id ||
    props?.patient?.patient_id ||
    props?.patient?.patientId;
  const fromStorage = (() => {
    try { return window.localStorage.getItem('currentPatientId') || null; } catch { return null; }
  })();
  const pid = fromSelected || fromProp || fromStorage || null;
  if (pid && pid !== fromStorage) {
    try { window.localStorage.setItem('currentPatientId', pid); } catch {}
  }
  return pid;
}

export default function FinancialTab({
  selectedPatient,
  formatCurrency,
  formatDate,
}) {
  const { user } = useAuth();
  const canUseFinancialAI = (user?.permissions || []).includes('ai.financial_use');
  const patientId = resolvePatientId({ selectedPatient });

  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [importedNfses, setImportedNfses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAIReview, setShowAIReview] = useState(true);

  const today = new Date();

  const currentMonthValue = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, '0')}`;

  const [periodMode, setPeriodMode] = useState('current_month');
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [showImportedNfseViewer, setShowImportedNfseViewer] = useState(false);
  const [selectedImportedNfse, setSelectedImportedNfse] = useState(null);

  const [walletSummary, setWalletSummary] = useState(null);
  const [walletMovements, setWalletMovements] = useState([]);

  const [walletFilter, setWalletFilter] = useState('all');

  const [showWalletModal, setShowWalletModal] = useState(false);
  const [savingWalletMovement, setSavingWalletMovement] = useState(false);
  const [walletForm, setWalletForm] = useState({
    movement_type: 'credit',
    amount: '',
    description: '',
    notes: '',
  });

  const fmtCurrency = (n) =>
    formatCurrency ? formatCurrency(n) : (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (d) =>
    formatDate ? formatDate(d) : d ? new Date(d).toLocaleString('pt-BR') : '';

  const getPeriodParams = () => {
    if (periodMode === 'all') {
      return {
        period_mode: 'all'
      };
    }

    if (periodMode === 'month') {
      const [year, month] = selectedMonth.split('-').map(Number);
      const lastDay = new Date(year, month, 0).getDate();

      return {
        period_mode: 'month',
        start_date: `${selectedMonth}-01`,
        end_date: `${selectedMonth}-${String(lastDay).padStart(2, '0')}`
      };
    }

    if (periodMode === 'custom') {
      return {
        period_mode: 'custom',
        start_date: customStartDate,
        end_date: customEndDate
      };
    }

    return {
      period_mode: 'current_month'
    };
  };

  useEffect(() => {
    let mounted = true;
    if (!patientId) {
      setLoading(false);
      setErr('Paciente não informado');
      return;
    }
    async function load() {
      if (
        periodMode === 'custom' &&
        (!customStartDate || !customEndDate)
      ) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setErr(null);
      try {
        const periodParams = getPeriodParams();
        const [invRes, payRes, nfseRes, walletRes, walletMovementsRes] = await Promise.all([
          api.get('/billing/invoices', {
            params: {
              patient_id: patientId,
              ...periodParams
            }
          }),
          api.get('/billing/payments', {
            params: {
              patient_id: patientId,
              ...periodParams
            }
          }),
          api.get('/imported-nfse', { params: { patient_id: patientId } }),
          api.get(`/billing/patients/${patientId}/wallet`),
          api.get(`/billing/patients/${patientId}/wallet/movements`),
        ]);
        const inv = Array.isArray(invRes?.data?.items) ? invRes.data.items :
                    Array.isArray(invRes?.data) ? invRes.data : [];
        const pay = Array.isArray(payRes?.data?.items) ? payRes.data.items :
                    Array.isArray(payRes?.data) ? payRes.data : [];
        const nfses = Array.isArray(nfseRes?.data?.items) ? nfseRes.data.items :
                      Array.isArray(nfseRes?.data) ? nfseRes.data : [];
        if (!mounted) return;
        setInvoices(inv);
        setPayments(pay);
        setImportedNfses(nfses);
        setWalletSummary(walletRes?.data || null);
        setWalletMovements(
          Array.isArray(walletMovementsRes?.data) ? walletMovementsRes.data : []
        );        
      } catch (e) {
        if (mounted) setErr('Falha ao carregar dados financeiros');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [
    patientId,
    periodMode,
    selectedMonth,
    customStartDate,
    customEndDate
  ]);

  const totals = useMemo(() => {
    const validInvoices = invoices.filter(
      (invoice) => invoice?.status !== 'cancelado'
    );

    const confirmedPayments = payments.filter(
      (payment) =>
        payment?.status === 'confirmado' &&
        payment?.invoice_id
    );

    const totalInvoices = validInvoices.reduce(
      (sum, invoice) => sum + (Number(invoice?.total_amount) || 0),
      0
    );

    const totalPaid = confirmedPayments.reduce(
      (sum, payment) => sum + (Number(payment?.amount) || 0),
      0
    );

    const paymentsByInvoice = confirmedPayments.reduce(
      (acc, payment) => {
        const invoiceId = payment?.invoice_id;
        if (!invoiceId) return acc;

        acc[invoiceId] =
          (acc[invoiceId] || 0) +
          (Number(payment?.amount) || 0);

        return acc;
      },
      {}
    );

    const totalOpen = validInvoices.reduce(
      (sum, invoice) => {
        if (
          invoice?.status === 'pago' ||
          invoice?.status === 'cancelado'
        ) {
          return sum;
        }

        const invoiceTotal = Number(invoice?.total_amount) || 0;
        const invoicePaid = paymentsByInvoice[invoice?.id] || 0;

        return sum + Math.max(invoiceTotal - invoicePaid, 0);
      },
      0
    );

    return {
      totalInvoices,
      totalPaid,
      totalOpen
    };
  }, [invoices, payments]);

  const reloadWallet = async () => {
    const [walletRes, movementsRes] = await Promise.all([
      api.get(`/billing/patients/${patientId}/wallet`),
      api.get(`/billing/patients/${patientId}/wallet/movements`),
    ]);

    setWalletSummary(walletRes?.data || null);
    setWalletMovements(Array.isArray(movementsRes?.data) ? movementsRes.data : []);
  };

  const handleSaveWalletMovement = async (e) => {
    e.preventDefault();

    const amount = Number(String(walletForm.amount || '').replace(',', '.'));

    if (!amount || amount <= 0) {
      alert('Informe um valor maior que zero.');
      return;
    }

    if (!walletForm.description.trim()) {
      alert('Informe uma descrição.');
      return;
    }

    try {
      setSavingWalletMovement(true);

      await api.post('/billing/patients/wallet/movements', {
        patient_id: patientId,
        patient_name: selectedPatient?.full_name || selectedPatient?.name || '',
        movement_type: walletForm.movement_type,
        origin: 'manual',
        amount,
        description: walletForm.description,
        notes: walletForm.notes,
      });

      setWalletForm({
        movement_type: 'credit',
        amount: '',
        description: '',
        notes: '',
      });

      setShowWalletModal(false);
      await reloadWallet();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Erro ao salvar movimentação.');
    } finally {
      setSavingWalletMovement(false);
    }
  };

  if (loading) return <div className="p-3 text-gray-600">Carregando…</div>;
  if (err) return <div className="p-3 text-red-600">{err}</div>;

  const filteredWalletMovements = walletMovements.filter((movement) => {
    if (walletFilter === 'all') return true;
    return movement.movement_type === walletFilter;
  });

  let runningBalance = 0;

  const walletHistory = [...filteredWalletMovements]
    .reverse()
    .map((item) => {
      const amount = Number(item.amount || 0);

      if (item.movement_type === 'credit') runningBalance += amount;
      if (item.movement_type === 'debit') runningBalance -= amount;
      if (item.movement_type === 'hold') runningBalance -= amount;
      if (item.movement_type === 'release_hold') runningBalance += amount;

      return {
        ...item,
        runningBalance,
      };
    })
    .reverse();

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">Resumo Financeiro</h3>
        {canUseFinancialAI && !showAIReview && (
          <button type="button" onClick={() => setShowAIReview(true)} className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100">
            <Sparkles className="h-4 w-4" /> Analisar com IA
          </button>
        )}
      </div>

      {canUseFinancialAI && showAIReview && <FinancialAIReviewCard patientId={patientId} onClose={() => setShowAIReview(false)} />}

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Período
            </label>

            <select
              value={periodMode}
              onChange={(e) => setPeriodMode(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
            >
              <option value="current_month">Mês atual</option>
              <option value="month">Mês específico</option>
              <option value="custom">Período personalizado</option>
              <option value="all">Todo o período</option>
            </select>
          </div>

          {periodMode === 'month' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mês
              </label>

              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}

          {periodMode === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data inicial
                </label>

                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data final
                </label>

                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
            </>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-3">
          O período é aplicado às faturas e aos pagamentos. O saldo da carteira continua acumulado.
        </p>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-5 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-purple-800 font-semibold">
              <WalletCards className="w-5 h-5" />
              Carteira / Crédito do Paciente
            </div>

            <p className="text-3xl font-bold text-purple-700 mt-2">
              {fmtCurrency(walletSummary?.available_balance || 0)}
            </p>

            <p className="text-sm text-purple-700 mt-1">
              Crédito: {fmtCurrency(walletSummary?.total_credit || 0)} · Usado:{' '}
              {fmtCurrency(walletSummary?.total_debit || 0)} · Bloqueado:{' '}
              {fmtCurrency(walletSummary?.total_hold || 0)}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowWalletModal(true)}
            className="inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova movimentação
          </button>
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-semibold text-purple-900 mb-2">
            Últimas movimentações
          </h4>

          <div className="flex flex-wrap gap-2 mb-3">

          <button
            type="button"
            onClick={()=>setWalletFilter('all')}
            className={`px-3 py-1 rounded-full text-xs ${
              walletFilter==='all'
                ? 'bg-purple-600 text-white'
                : 'bg-white border'
            }`}
          >
          Todas
          </button>

          <button
            type="button"
            onClick={()=>setWalletFilter('credit')}
            className={`px-3 py-1 rounded-full text-xs ${
              walletFilter==='credit'
                ? 'bg-green-600 text-white'
                : 'bg-white border'
            }`}
          >
          Créditos
          </button>

          <button
            type="button"
            onClick={()=>setWalletFilter('debit')}
            className={`px-3 py-1 rounded-full text-xs ${
              walletFilter==='debit'
                ? 'bg-red-600 text-white'
                : 'bg-white border'
            }`}
          >
          Débitos
          </button>

          <button
            type="button"
            onClick={()=>setWalletFilter('hold')}
            className={`px-3 py-1 rounded-full text-xs ${
              walletFilter==='hold'
                ? 'bg-yellow-600 text-white'
                : 'bg-white border'
            }`}
          >
          Reservas
          </button>

          </div>

          {walletHistory.length > 0 ? (
            <div className="space-y-2">
              {walletHistory.map((movement) => {
                const isCredit = movement.movement_type === 'credit';
                const isHold = movement.movement_type === 'hold';
                const isReleaseHold = movement.movement_type === 'release_hold';

                return (
                  <div
                    key={movement.id}
                    className="bg-white border border-purple-100 rounded-md p-3 flex justify-between gap-3"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {movement.description}
                      </p>

                      <p className="text-xs text-gray-500">
                        Origem: {movement.origin || 'manual'}
                      </p>

                      <p className="text-xs text-gray-500">
                        {fmtDate(movement.created_at)}
                        {movement.notes ? ` · ${movement.notes}` : ''}
                      </p>
                    </div>

                    <div className="text-right">
                      <div
                        className={`font-bold ${
                          isCredit || isReleaseHold
                            ? 'text-green-600'
                            : isHold
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {isCredit || isReleaseHold ? '+' : isHold ? (
                          <Lock className="w-4 h-4 inline mr-1" />
                        ) : (
                          '-'
                        )}
                        {fmtCurrency(movement.amount)}
                      </div>

                      <p className="text-xs text-gray-500 mt-1">
                        Saldo:{' '}
                        <strong>{fmtCurrency(movement.runningBalance)}</strong>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-purple-700">
              Nenhuma movimentação de crédito registrada.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-blue-50 p-6 rounded-lg text-center border">
          <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-blue-600">
            {fmtCurrency(totals.totalInvoices)}
          </p>
          <p className="text-sm text-blue-600">Total Faturado</p>
        </div>
        <div className="bg-green-50 p-6 rounded-lg text-center border">
          <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-green-600">
            {fmtCurrency(totals.totalPaid)}
          </p>
          <p className="text-sm text-green-600">Total Pago</p>
        </div>
        <div className="bg-yellow-50 p-6 rounded-lg text-center border">
          <Receipt className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-yellow-600">
            {fmtCurrency(totals.totalOpen)}
          </p>
          <p className="text-sm text-yellow-600">Em aberto</p>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">
          Faturas ({invoices.length})
        </h4>
        {invoices.length > 0 ? (
          <div className="space-y-3">
            {invoices.map((invoice, idx) => (
              <div key={invoice?.id || idx} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center border">
                <div>
                  <p className="font-medium">Fatura #{invoice.number || idx + 1}</p>
                  <p className="text-sm text-gray-600">Data: {fmtDate(invoice?.created_at)}</p>
                </div>
                <span className="font-bold text-gray-900">{fmtCurrency(invoice?.total_amount)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-4 bg-gray-50 rounded-lg">
            Nenhuma fatura registrada
          </p>
        )}
      </div>

      <div>
        <h4 className="text-md font-semibold text-gray-900 mb-3">
          Pagamentos ({payments.length})
        </h4>
        {payments.length > 0 ? (
          <div className="space-y-3">
            {payments.map((payment, idx) => (
              <div key={payment?.id || idx} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center border">
                <div>
                  <p className="font-medium">Pagamento</p>
                  <p className="text-sm text-gray-600">Data: {fmtDate(payment?.created_at)}</p>
                </div>
                <span className="font-bold text-green-600">{fmtCurrency(payment?.amount)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-4 bg-gray-50 rounded-lg">
            Nenhum pagamento registrado
          </p>
        )}
      </div>

      <div className="mt-8">
        <h4 className="text-md font-semibold text-gray-900 mb-3">
          NFS-e Importadas ({importedNfses.length})
        </h4>
        {importedNfses.length > 0 ? (
          <div className="space-y-3">
            {importedNfses.map((nfse, idx) => (
              <div key={nfse?.id || idx} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center border">
                <div>
                  <p className="font-medium">NFS-e #{nfse.numero_nfse || nfse.id}</p>
                  <p className="text-sm text-gray-600">Emissão: {fmtDate(nfse?.data_emissao)} | Tomador: {nfse.tomador_nome || '—'}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="font-bold text-gray-900">{fmtCurrency(nfse?.valor_servico)}</span>
                  <button 
                    onClick={() => { setSelectedImportedNfse(nfse); setShowImportedNfseViewer(true); }}
                    className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                  >
                    Ver detalhes
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center py-4 bg-gray-50 rounded-lg">
            Nenhuma NFS-e importada para este paciente
          </p>
        )}
      </div>

      {showWalletModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowWalletModal(false);
            }
          }}
        >
          <div
            className="bg-white rounded-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                Nova movimentação da carteira
              </h3>
              <button
                type="button"
                onClick={() => setShowWalletModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveWalletMovement} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={walletForm.movement_type}
                  onChange={(e) =>
                    setWalletForm((p) => ({ ...p, movement_type: e.target.value }))
                  }
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="credit">Adicionar crédito</option>
                  <option value="debit">Abater crédito</option>
                  <option value="hold">Bloquear/reservar crédito</option>
                  <option value="release_hold">Liberar crédito bloqueado</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={walletForm.amount}
                  onChange={(e) =>
                    setWalletForm((p) => ({ ...p, amount: e.target.value }))
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ex.: 5000,00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <input
                  type="text"
                  value={walletForm.description}
                  onChange={(e) =>
                    setWalletForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Ex.: Pagamento antecipado"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={walletForm.notes}
                  onChange={(e) =>
                    setWalletForm((p) => ({ ...p, notes: e.target.value }))
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  rows="3"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowWalletModal(false)}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={savingWalletMovement}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg disabled:opacity-60"
                >
                  {savingWalletMovement ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportedNfseViewer && selectedImportedNfse && (
        <ImportedNfseViewer
          nfse={selectedImportedNfse}
          onClose={() => {
            setShowImportedNfseViewer(false);
            setSelectedImportedNfse(null);
          }}
        />
      )}
    </div>
  );
}
